// src/utils/historiqueChamps.ts — SCE Issues
// Historique des corrections d'un problème, champ par champ (23/10, lot 1).
//
// Une CORRECTION (titre, description, phase, N° de plan, schéma électrique)
// n'écrit plus d'événement « Modification » : elle encombrait la fiche et
// l'activité récente de tout le monde pour une faute de frappe. Elle se lit
// dans l'historique des versions de SharePoint (activé sur SCE_Problemes), par
// une icône à côté du champ. Ce que quelqu'un d'autre doit savoir ou faire
// (métier, sujet, équipement, famille, criticité, statut, qualité…) reste un
// événement : cf. CHAMPS_SANS_EVENEMENT.
//
// Pur : la lecture réseau est dans api/issues.api (historiqueProbleme).

import { versionsDuCommentaire, type VersionCommentaire } from './editionCommentaire'
import type { Changement, ChampModifiable } from './modificationIssue'

export type ChampHistorise = 'titre' | 'description' | 'phase' | 'numeroPlan' | 'ede'

export const LIBELLES_HISTORIQUE: Record<ChampHistorise, string> = {
  titre: 'Titre',
  description: 'Description',
  phase: 'Phase',
  numeroPlan: 'N° de plan',
  ede: 'Schéma électrique',
}

/** Une version de la ligne : sa date et la valeur de chaque champ suivi ('' : vide). */
export interface VersionProbleme {
  date: string
  champs: Record<ChampHistorise, string>
  /** Qui a écrit la version (« Prénom Nom ») : celle où une valeur apparaît dit qui l'a mise. */
  par?: string
}

/** Les valeurs successives de chaque champ, la plus récente d'abord. */
export type HistoriqueProbleme = Record<ChampHistorise, VersionCommentaire[]>

const CHAMPS: readonly ChampHistorise[] = ['titre', 'description', 'phase', 'numeroPlan', 'ede']

/** Corrections de la fiche qui ne s'annoncent plus dans l'activité : leur historique est sur le champ. */
export const CHAMPS_SANS_EVENEMENT: ReadonlySet<ChampModifiable> = new Set(['titre', 'description', 'phase'])

/** Les changements à écrire dans l'événement « Modification » (vide : pas d'événement). */
export function changementsAnnonces(changements: readonly Changement[]): Changement[] {
  return changements.filter((c) => !CHAMPS_SANS_EVENEMENT.has(c.champ))
}

export function historiqueDesChamps(versions: readonly VersionProbleme[]): HistoriqueProbleme {
  const h = {} as HistoriqueProbleme
  for (const champ of CHAMPS) {
    h[champ] = versionsDuCommentaire(
      versions.map((v) => ({ date: v.date, contenu: v.champs[champ], ...(v.par ? { par: v.par } : {}) })),
    )
  }
  return h
}

/** Le champ a-t-il changé depuis la déclaration ? (une seule valeur : jamais touché) */
export function estCorrige(h: HistoriqueProbleme | null, champ: ChampHistorise): boolean {
  return (h?.[champ].length ?? 0) > 1
}

/** « NVS50312-G · Rev 01 · p. 12-14 » ; '' sans N°. */
export function texteEde(numero: string | undefined, rev: string | undefined, pages: string | undefined): string {
  const n = (numero ?? '').trim()
  if (!n) return ''
  return [n, rev?.trim() ? `Rev ${rev.trim()}` : '', pages?.trim() ? `p. ${pages.trim()}` : ''].filter(Boolean).join(' · ')
}
